import { NextPage } from 'next'
import { OverseasCompanyData } from '../../../types/api'
import Error from '../../../pages/_error'
import { useState } from 'react'
import { OverseasCompanySummary } from './molecules/oversea/OverseasCompanySummary'
import { OverseasCompanyOverview } from './molecules/oversea/OverseasCompanyOverview'
import { Template } from '../../common/Template'
import { colors, mq } from 'plume-ui'
import { css } from '@emotion/react'
import { A } from '../../atoms/A'
import { OverseasCompanyHead } from '../../common/Head'
import { OverseasIpoCompanyOverview } from './molecules/oversea/OverseasIpoCompanyOverview'
import { OverseasCompanyFinance } from './molecules/oversea/OverseasCompanyFinance'
import { OverseasCompanyTabGroup } from './molecules/oversea/OverseasCompanyTabGroup'
import { CompanyTabType } from 'types'
import { CompanyInviewForActiveTab } from './molecules/domestic/CompanyInviewForActiveTab'
import {
  ActiveTabMap,
  createInViewEventQueue,
  setupActiveTabs,
} from '@/components/common/InviewForActiveTab'

export const overseaCompanyTabList: CompanyTabType[] = [
  'basic-info',
  'finance-info',
]

const [push, apply] = createInViewEventQueue()

type Props = {
  company: Required<OverseasCompanyData> | null
}

const OverseasCompanyPage: NextPage<Props> = (props) => {
  const [init, first] = setupActiveTabs(overseaCompanyTabList)
  const [activeTabs, setActiveTabs] =
    useState<ActiveTabMap<CompanyTabType>>(init())

  const handleChangeActive = (tab: CompanyTabType, inView: boolean) => {
    push(tab, inView, onTimeout)
  }

  const onTimeout = () => {
    setActiveTabs(apply(activeTabs))
  }

  if (!props.company) {
    return <Error />
  }

  return (
    <>
      <OverseasCompanyHead company={props.company}>
        <meta name="viewport" content="width=1200px" key="viewport" />
      </OverseasCompanyHead>
      <Template>
        <OverseasCompanySummary company={props.company} />
        <OverseasCompanyTabGroup activeTab={first(activeTabs)} />
        <div
          css={css`
            background-color: ${colors.gray40};
            padding-bottom: 55px;
          `}
        >
          <div
            css={css`
              max-width: 1200px;
              margin: 0 auto;
            `}
          >
            <div
              css={css`
                padding-bottom: 2rem;
              `}
            >
              <CompanyInviewForActiveTab
                tabName="basic-info"
                onChangeActive={handleChangeActive}
                offset="-5rem"
                mobileOffset="-5rem"
                css={css`
                  padding-block-start: 24px;
                  ${mq.untilMedium} {
                    padding-block: 0px;
                  }
                `}
              >
                {props.company.listed ? (
                  <OverseasIpoCompanyOverview company={props.company} />
                ) : (
                  <OverseasCompanyOverview company={props.company} />
                )}
              </CompanyInviewForActiveTab>
              <CompanyInviewForActiveTab
                tabName="finance-info"
                onChangeActive={handleChangeActive}
                offset="-5rem"
                mobileOffset="-5rem"
                css={css`
                  padding-block-start: 40px;
                  ${mq.untilMedium} {
                    padding-top: 0px;
                  }
                `}
              >
                <OverseasCompanyFinance
                  overseaFundingRounds={props.company.overseaFundingRounds}
                />
              </CompanyInviewForActiveTab>
            </div>
            <div
              css={css`
                display: flex;
                justify-content: center;
              `}
            >
              <div
                className="powered-by"
                css={css`
                  font-size: 14px;
                `}
              >
                Powered by{' '}
                {props.company.crunchbaseUrl ? (
                  <A
                    href={props.company.crunchbaseUrl}
                    decoration="none"
                    target="_blank"
                  >
                    Crunchbase
                  </A>
                ) : (
                  'Crunchbase'
                )}
              </div>
            </div>
          </div>
        </div>
      </Template>
    </>
  )
}

export default OverseasCompanyPage
